// @system — form field primitives with mobile-optimized inputs
import { forwardRef, useId, isValidElement } from 'react'
import { describeControl } from '@/app/lib/@system/a11y'
import { Label } from '@radix-ui/react-label'
import { cn } from '@/app/lib/@system/utils'
import { Honeypot } from '@/app/components/@system/Honeypot'

/**
 * @typedef {object} FormFieldProps
 * @property {React.ReactNode} [label]
 * @property {React.ReactNode} [error] - Validation message; also wires aria-invalid / aria-describedby on the control
 * @property {boolean} [required]
 * @property {React.ReactNode} [children] - The control (its `id` is reused when present)
 * @property {string} [className]
 * @property {string} [id] - Control id; defaults to the child's `id`, then an auto id
 */

/** @param {FormFieldProps} props */
function FormField({ label, error, required, children, className, id: idProp }) {
  const autoId = useId()
  const id = idProp ?? (isValidElement(children) && children.props?.id) ?? `field-${autoId}`
  const errorId = `${id}-error`
  return (
    <div className={cn('space-y-2', className)}>
      {label && (
        <Label htmlFor={id} className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
          {label}
          {required && <span className="text-[var(--color-error)] ml-1" aria-hidden="true">*</span>}
        </Label>
      )}
      {describeControl(children, { id, errorId, error })}
      {error && <p id={errorId} role="alert" className="text-xs sm:text-sm text-[var(--color-error)] mt-1">{error}</p>}
    </div>
  )
}

/** @typedef {{ error?: boolean | string | null }} ControlErrorProp - truthy = error styling */

/** @type {ForwardRefComponent<'input', ControlErrorProp>} */
const Input = forwardRef(function Input({ className, error, ...props }, ref) {
  return (
    <input
      ref={ref}
      className={cn(
        // Base styles
        'flex w-full rounded-md border border-brand-border bg-brand-bg px-3 py-2 text-sm sm:text-base ring-offset-brand-bg',
        // Mobile-friendly height (min 44px for touch targets)
        'h-11 sm:h-10',
        // File input
        'file:border-0 file:bg-transparent file:text-sm file:font-medium',
        // Placeholder
        'placeholder:text-brand-text-muted',
        // Focus states
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-offset-2',
        // Disabled states
        'disabled:cursor-not-allowed disabled:opacity-50',
        // Error state
        error && 'border-destructive focus-visible:ring-destructive',
        className
      )}
      {...props}
    />
  )
})

/** @type {ForwardRefComponent<'textarea', ControlErrorProp>} */
const Textarea = forwardRef(function Textarea({ className, error, ...props }, ref) {
  return (
    <textarea
      ref={ref}
      className={cn(
        // Base styles
        'flex min-h-[80px] w-full rounded-md border border-brand-border bg-brand-bg px-3 py-2 text-sm sm:text-base ring-offset-brand-bg',
        // Placeholder
        'placeholder:text-brand-text-muted',
        // Focus states
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-offset-2',
        // Disabled states
        'disabled:cursor-not-allowed disabled:opacity-50',
        // Error state
        error && 'border-destructive focus-visible:ring-destructive',
        // Prevent resize on mobile for better UX
        'resize-y sm:resize',
        className
      )}
      {...props}
    />
  )
})

export { FormField, Input, Textarea }

// Simple Form wrapper — every form carries the spam honeypot (components/@system/Honeypot,
// launch contract rule 18); handlers reading the form with FormData get an empty `website`.
/** @param {import('react').FormHTMLAttributes<HTMLFormElement>} props */
function Form({ onSubmit, children, ...props }) {
  return (
    <form onSubmit={onSubmit} {...props}>
      <Honeypot />
      {children}
    </form>
  )
}

// Simple FormLabel
/** @param {import('react').LabelHTMLAttributes<HTMLLabelElement>} props */
function FormLabel({ children, ...props }) {
  return <label className="text-sm font-medium leading-none" {...props}>{children}</label>
}

export { Form, FormLabel }
